/** The Sketch's notes in the side panel: the list, the open note (its text, where it is printed, Done), and a new
 * note being pinned. Presentational; data in hooks.ts (`useSketchNotes`), logic in notes.ts. Styles: `.sk-notes…`
 * in sketch.css. */
import { Check, FileText, MapPin, X } from "lucide-react";
import { useEffect, useState } from "react";
import { printedIn, type SketchNote } from "./notes.ts";
import type { Text } from "./text.ts";
import { kbd, Msg, useSketchText } from "./useText.ts";

export interface NoteDraft {
  at: [number, number];
  faceId: string | null;
}

/** Where a note is: on a face (its name), on a dormer, or at a spot (null). */
export type NotePlace = { kind: "face"; name: string } | { kind: "dormer" } | null;

function placeText(tx: Text, place: NotePlace, open: boolean): string {
  if (place?.kind === "face") return tx.t(open ? "sketch.notes.onFace" : "sketch.notes.newOnFace", { face: place.name });
  if (place?.kind === "dormer") return tx.t(open ? "sketch.notes.onDormer" : "sketch.notes.newOnDormer");
  return tx.t("sketch.notes.atSpot");
}

export function NotesSection(props: {
  notes: SketchNote[];
  selected: string | null;
  draft: NoteDraft | null;
  faceLabel: (faceId: string | null) => NotePlace;
  readOnly: boolean;
  busy: boolean;
  error: string | null;
  onSelect: (id: string | null) => void;
  onCreate: (text: string, print: { proposal: boolean; planSet: boolean }) => void;
  onCancelDraft: () => void;
  onChange: (id: string, change: { body?: string; in_proposal?: boolean; in_plan_set?: boolean }) => void;
  onDone: (id: string) => void;
  onAdd: () => void;
}) {
  const tx = useSketchText();
  const open = props.notes.find((x) => x.id === props.selected) ?? null;
  if (!props.notes.length && !props.draft)
    return (
      <section className="sk-side-section sk-notes" aria-label={tx.t("sketch.tool.note")}>
        <div className="sk-side-head">
          <span className="sk-side-icon"><MapPin size={16} /></span>
          <h3>{tx.t("sketch.tool.note")}</h3>
        </div>
        <p className="sk-muted">{tx.t("sketch.notes.empty")}</p>
        {!props.readOnly && (
          <button type="button" className="sk-action" onClick={props.onAdd}><MapPin size={14} /> <Msg id="sketch.notes.add" values={{ kbd }} /></button>
        )}
      </section>
    );
  return (
    <section className="sk-side-section sk-notes" aria-label={tx.t("sketch.tool.note")}>
      <div className="sk-side-head">
        <span className="sk-side-icon"><MapPin size={16} /></span>
        <h3>{tx.t("sketch.tool.note")}</h3>
        <span className="sk-eyebrow">{props.notes.length}</span>
      </div>
      {props.draft && <DraftNote draft={props.draft} faceLabel={props.faceLabel(props.draft.faceId)} busy={props.busy} onCreate={props.onCreate} onCancel={props.onCancelDraft} />}
      {open && <OpenNote key={open.id} note={open} faceLabel={props.faceLabel(open.faceId)} readOnly={props.readOnly} onChange={props.onChange} onDone={props.onDone} onClose={() => props.onSelect(null)} />}
      {props.error && <p className="sk-error">{props.error}</p>}
      <ol className="sk-notes-list">
        {props.notes.map((x) => (
          <li key={x.id}>
            <button type="button" className={`sk-note-row${x.id === props.selected ? " is-active" : ""}`} onClick={() => props.onSelect(x.id === props.selected ? null : x.id)}>
              <span className="sk-note-n">{x.n}</span>
              <span className="sk-note-text" dir="auto">{x.text}</span>
              {(x.inProposal || x.inPlanSet) && <FileText size={13} className="sk-note-printed" aria-label={tx.t("sketch.notes.printedIn", { where: tx.t(printedIn(x)) })} />}
            </button>
          </li>
        ))}
      </ol>
      {!props.readOnly && !props.draft && (
        <button type="button" className="sk-action is-ghost" onClick={props.onAdd}><MapPin size={14} /> <Msg id="sketch.notes.add" values={{ kbd }} /></button>
      )}
    </section>
  );
}

function PrintSwitches({ proposal, planSet, disabled, onChange }: { proposal: boolean; planSet: boolean; disabled?: boolean; onChange: (p: { proposal: boolean; planSet: boolean }) => void }) {
  const tx = useSketchText();
  return (
    <fieldset className="sk-note-print" disabled={disabled}>
      <legend>{tx.t("sketch.notes.printIn")}</legend>
      <label><input type="checkbox" checked={proposal} onChange={(e) => onChange({ proposal: e.target.checked, planSet })} /> {tx.t("sketch.notes.printedProposal")}</label>
      <label><input type="checkbox" checked={planSet} onChange={(e) => onChange({ proposal, planSet: e.target.checked })} /> {tx.t("sketch.notes.printedPlanSet")}</label>
    </fieldset>
  );
}

function DraftNote({ draft, faceLabel, busy, onCreate, onCancel }: { draft: NoteDraft; faceLabel: NotePlace; busy: boolean; onCreate: (text: string, print: { proposal: boolean; planSet: boolean }) => void; onCancel: () => void }) {
  const tx = useSketchText();
  const [text, setText] = useState("");
  const [print, setPrint] = useState({ proposal: false, planSet: false });
  return (
    <div className="sk-note-card is-new">
      <div className="sk-note-where"><MapPin size={13} /> {faceLabel ? placeText(tx, faceLabel, false) : tx.t("sketch.notes.newAt", { x: tx.num(draft.at[0], 1), y: tx.num(draft.at[1], 1) })}</div>
      <textarea className="sk-note-input" rows={3} maxLength={4000} autoFocus dir="auto" placeholder={tx.t("sketch.notes.placeholder")} value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => {
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && text.trim()) onCreate(text, print);
        if (e.key === "Escape") onCancel();
      }} />
      <PrintSwitches proposal={print.proposal} planSet={print.planSet} onChange={setPrint} />
      <div className="sk-actions">
        <button type="button" className="sk-action is-primary" disabled={!text.trim() || busy} onClick={() => onCreate(text, print)}><Check size={14} /> {tx.t("sketch.notes.pin")}</button>
        <button type="button" className="sk-action is-ghost" onClick={onCancel}>{tx.t("sketch.common.cancel")}</button>
      </div>
    </div>
  );
}

function OpenNote({ note, faceLabel, readOnly, onChange, onDone, onClose }: { note: SketchNote; faceLabel: NotePlace; readOnly: boolean; onChange: (id: string, change: { body?: string; in_proposal?: boolean; in_plan_set?: boolean }) => void; onDone: (id: string) => void; onClose: () => void }) {
  const tx = useSketchText();
  const [text, setText] = useState(note.text);
  useEffect(() => setText(note.text), [note.text]);
  return (
    <div className="sk-note-card">
      <div className="sk-note-where">
        <span className="sk-note-n">{note.n}</span>
        {tx.t("sketch.notes.where", { place: placeText(tx, faceLabel, true), author: note.author })}
        <button type="button" className="sk-icon-action" aria-label={tx.t("sketch.notes.close")} onClick={onClose}><X size={14} /></button>
      </div>
      <textarea className="sk-note-input" rows={3} maxLength={4000} dir="auto" value={text} disabled={readOnly} onChange={(e) => setText(e.target.value)} onBlur={() => text.trim() && text.trim() !== note.text && onChange(note.id, { body: text.trim() })} />
      <PrintSwitches proposal={note.inProposal} planSet={note.inPlanSet} disabled={readOnly} onChange={(p) => onChange(note.id, { in_proposal: p.proposal, in_plan_set: p.planSet })} />
      {note.replies > 0 && <p className="sk-muted">{tx.t("sketch.notes.replies", { count: note.replies })}</p>}
      {!readOnly && (
        <div className="sk-actions">
          <button type="button" className="sk-action is-ghost" onClick={() => onDone(note.id)} title={tx.t("sketch.notes.doneTitle")}><Check size={14} /> {tx.t("sketch.common.done")}</button>
        </div>
      )}
    </div>
  );
}
